import dayjs from "dayjs"
import useSWR from "swr"
import { DeliveryRouteApi } from "../../apis"
import { IDeliveryRoute } from "../../apis/delivery-route"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import type { DeliveryActionSubject } from "../delivery/delivery-actions"

/**
 * GET /delivery_note/:id (contract §2): the receiver is the BUYER (`receiverBusiness`), with its branch,
 * a dialable phone, and the server's status preconditions for the manager actions.
 */
export type DnDetail = IDeliveryNotePlus & {
  receiverPhone?: string | null
  hasReceipt?: boolean
  allowedActions?: DeliveryActionSubject["allowedActions"]
  pdf?: string | null
}

export type DnPriority = "VIP" | "URGENT" | "NORMAL"

export const formatMoney = (value?: number | null): string => (value == null ? "-" : `${value.toLocaleString()}₮`)

export const formatDateTime = (value?: string | null): string => (value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-")

export function dnPriority(dn: DnDetail): DnPriority {
  if ((dn.sla?.priority as string) === "VIP") return "VIP"
  if ((dn.totalAmount ?? 0) >= 10_000_000) return "VIP"
  if (dn.deliveryDate && dayjs(dn.deliveryDate).diff(dayjs(), "hour") < 24) return "URGENT"
  return "NORMAL"
}

/** The driver is on the road (or about to be): GPS can be live. */
export const isLiveStatus = (status?: string | null) => status === "DELIVERING" || status === "LOADED" || status === "PENDING_HANDOVER"

export type DriverStage = "DISPATCH" | "CURRENT" | "DELIVERED"

export type DriverSnapshot = {
  lat: number | null
  lng: number | null
  stage: DriverStage
  recordedAt: string | null
}

/** Freshest driver GPS point: delivered > current > dispatch > legacy last ping. */
export function driverSnapshot(dn: DnDetail): DriverSnapshot {
  if (dn.deliveredLatitude != null && dn.deliveredLongitude != null) {
    return { lat: dn.deliveredLatitude, lng: dn.deliveredLongitude, stage: "DELIVERED", recordedAt: dn.deliveredRecordedAt ?? null }
  }
  if (dn.currentLatitude != null && dn.currentLongitude != null) {
    return { lat: dn.currentLatitude, lng: dn.currentLongitude, stage: "CURRENT", recordedAt: dn.currentRecordedAt ?? null }
  }
  if (dn.dispatchLatitude != null && dn.dispatchLongitude != null) {
    return { lat: dn.dispatchLatitude, lng: dn.dispatchLongitude, stage: "DISPATCH", recordedAt: dn.dispatchRecordedAt ?? null }
  }
  if (dn.lastDriverLat != null && dn.lastDriverLng != null) {
    return { lat: dn.lastDriverLat, lng: dn.lastDriverLng, stage: "CURRENT", recordedAt: dn.lastDriverPingAt ?? null }
  }
  return { lat: null, lng: null, stage: "CURRENT", recordedAt: null }
}

/**
 * Routes that contain this DN (list endpoint embeds stops when filtered by `deliveryNoteId`). Shared by the
 * Маршрут tab and the driver/vehicle card: the vehicle lives on the route, not on the DN (O96).
 */
export function useDnRoutes(dnId: string | null) {
  const { data, isLoading } = useSWR(
    dnId ? ["dn-detail.route-lookup", dnId] : null,
    () => DeliveryRouteApi.list({ filter: { deliveryNoteId: dnId as string }, offset: { limit: 10, skip: 0 } }),
    { revalidateOnFocus: false },
  )
  const routes: IDeliveryRoute[] = data?.rows ?? []
  return { routes, isLoading }
}
